// this whole page is only rendered on the checks route
/* eslint-disable local/no-direct-route-config-access */
import { gql, useQuery } from '@apollo/client';
import IconBook from '@apollo/icons/default/IconBook.svg';
import IconBranch from '@apollo/icons/default/IconBranch.svg';
import IconCommit from '@apollo/icons/default/IconCommit.svg';
import IconGitHubSolid from '@apollo/icons/default/IconGitHubSolid.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconRover from '@apollo/icons/default/IconRover.svg';
import IconSettings from '@apollo/icons/default/IconSettings.svg';
import IconTeam from '@apollo/icons/default/IconTeam.svg';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardHeaderControl,
  CardTitle,
  Divider,
  IconButton,
  Link,
  Switch,
} from '@apollo/orbit';
import { Astronaut2 } from '@apollo/space-kit/illustrations/Astronaut2';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import { LocationDescriptorObject } from 'history';
import { uniq } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import {
  TaskStatusIcon,
  TaskStatusIconTaskFragment,
} from 'src/app/graph/checksPage/taskStatusIcon/TaskStatusIcon';
import {
  graphSettingsRouteConfig,
  recentChecksRouteConfig,
  variantSettingsRouteConfig,
} from 'src/app/graph/routes';
import { committerDisplayName } from 'src/app/shared/commiterDisplayName';
import { Code } from 'src/components/code/Code';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { DocsRouter } from 'src/lib/routers';

import { graphRefToString } from '../../hooks/useGraphRef';

import { ChecksDisabledAlertBanner } from './checksDisabledAlertBanner/ChecksDisabledAlertBanner';
import {
  CheckWorkflowItem,
  CheckWorkflowItemFragment,
} from './checkWorkflowItem/CheckWorkflowItem';
import {
  CheckWorkflowTaskItem,
  CheckWorkflowTaskItemFragment,
} from './checkWorkflowItem/checkWorkflowTaskItem/CheckWorkflowTaskItem';
import { DownstreamCheckWorkflowList } from './checkWorkflowItem/DownstreamCheckWorkflowList';
import { shouldDisplayTask } from './checkWorkflowItem/getTaskDisplayStatus';
import {
  SuccessfulCheckWorkflowItem,
  SuccessfulCheckWorkflowItemFragment,
} from './checkWorkflowItem/SuccessfulCheckWorkflowItem';

const CheckWorkflowsListPageWorkflowFragment = gql`
  fragment CheckWorkflowsListPageWorkflowFragment on CheckWorkflow {
    id
    ...CheckWorkflowItemFragment
    ...SuccessfulCheckWorkflowItemFragment
    tasks {
      id
      ...CheckWorkflowTaskItemFragment
      ...TaskStatusIconTaskFragment
    }
  }
  ${CheckWorkflowItemFragment}
  ${SuccessfulCheckWorkflowItemFragment}
  ${CheckWorkflowTaskItemFragment}
  ${TaskStatusIconTaskFragment}
`;

const checkWorkflowsListPageQuery = gql`
  query CheckWorkflowsListPageQuery(
    $graphId: ID!
    $graphVariant: String!
    $checkWorkflowsFilter: CheckFilterInput!
    $proposalsUseHasWarnings: Boolean!
  ) {
    service(id: $graphId) {
      id
      roles {
        canQueryCheckConfiguration
      }
      checkWorkflows(filter: $checkWorkflowsFilter) {
        id
        ...CheckWorkflowsListPageWorkflowFragment
      }
      # Used to check whether checks exist for this service
      # if filters are not set
      singleUnfilteredCheckWorkflow: checkWorkflows(limit: 1) {
        id
      }
      checkConfiguration {
        id
        enableLintChecks
      }
      variant(name: $graphVariant) {
        id
        hasManagedSubgraphs
        checkConfiguration {
          id
          operationsChecksEnabled
        }
      }
    }
  }
  ${CheckWorkflowsListPageWorkflowFragment}
`;

const checkWorkflowsListPagePollingQuery = gql`
  query CheckWorkflowsListPagePollingQuery(
    $graphId: ID!
    $checkWorkflowsFilter: CheckFilterInput!
    $pendingFilters: CheckFilterInput!
    $proposalsUseHasWarnings: Boolean!
  ) {
    service(id: $graphId) {
      id
      checkWorkflows(filter: $checkWorkflowsFilter) {
        id
      }
      pendingCheckWorkflows: checkWorkflows(filter: $pendingFilters) {
        id
        ...CheckWorkflowsListPageWorkflowFragment
      }
      # Used to check whether checks exist for this service
      # if filters are not set
      singleUnfilteredCheckWorkflow: checkWorkflows(limit: 1) {
        id
      }
    }
  }
  ${CheckWorkflowsListPageWorkflowFragment}
`;

enum WorkflowAuthorTypes {
  GIT,
  USER,
}

export const CheckWorkflowsListPage = () => {
  const {
    graphId,
    graphVariant,
    author,
    branch,
    subgraph,
    status,
    variants,
    view,
  } = useRouteParams(recentChecksRouteConfig);
  const shouldDescriptionsWarn = useLDFlag(
    'gov-proposal-checks-descriptions-warn',
  );
  const proposalsUseHasWarnings = useLDFlag(
    'tooling-proposals-use-has-warnings',
  );

  const location = useLocation();
  const selectedAuthors = typeof author === 'string' ? [author] : author;
  const selectedSubgraphs =
    typeof subgraph === 'string' ? [subgraph] : subgraph;
  const selectedBranches = typeof branch === 'string' ? [branch] : branch;
  const isVariantTab = view === 'variant';
  const selectedVariants = isVariantTab
    ? [graphVariant]
    : typeof variants === 'string'
      ? [variants]
      : variants;

  const checkWorkflowFilter = {
    authors: selectedAuthors,
    subgraphs: selectedSubgraphs,
    branches: selectedBranches,
    variants: selectedVariants,
    status,
  };
  const { data, loading, error } = useQuery<
    GraphQLTypes.CheckWorkflowsListPageQuery,
    GraphQLTypes.CheckWorkflowsListPageQueryVariables
  >(checkWorkflowsListPageQuery, {
    variables: {
      proposalsUseHasWarnings,
      graphId,
      graphVariant,
      checkWorkflowsFilter: checkWorkflowFilter,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const pendingIds = useMemo(
    () =>
      data?.service?.checkWorkflows
        .filter((w) => w.status === 'PENDING')
        .map((w) => w.id) ?? [],
    [data],
  );
  /**
   * Once a workflow has PASSED or FAILED, it becomes immutable. We don't need to
   * poll for updates on these, but we do want to poll for PENDING workflows to
   * show updates to them.
   *
   * This splits out the query to update the cache with the new values here,
   * we don't need to do anything with these results because the cache will update
   * and the above query will pick up the new data.
   */
  const { error: pollingError } = useQuery<
    GraphQLTypes.CheckWorkflowsListPagePollingQuery,
    GraphQLTypes.CheckWorkflowsListPagePollingQueryVariables
  >(checkWorkflowsListPagePollingQuery, {
    variables: {
      proposalsUseHasWarnings,
      graphId,
      pendingFilters: { ids: pendingIds },
      checkWorkflowsFilter: checkWorkflowFilter,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    pollInterval: 5000,
  });

  const [showSandboxChecks, setShowSandboxChecks] = useState(true);
  const [expandedWorkflowIds, setExpandedWorkflowIds] = useState(
    new Set<string>(),
  );

  const operationsChecksEnabled =
    data?.service?.variant?.checkConfiguration.operationsChecksEnabled;
  const enableLintChecks = data?.service?.checkConfiguration?.enableLintChecks;
  const canQueryCheckConfiguration =
    data?.service?.roles?.canQueryCheckConfiguration;

  // initially show the banner if operations checks are disabled and we're on the variant tab
  // need to explicitely check for false because on load, operationsChecksEnabled could be null
  const showOpChecksDisabledAlertBanner =
    operationsChecksEnabled === false && isVariantTab;
  const showLintChecksDisabledAlertBanner =
    enableLintChecks === false && !isVariantTab;
  const showOpAndLintChecksDisabledAlertBanner =
    showOpChecksDisabledAlertBanner && enableLintChecks === false;

  const workflows = data?.service?.checkWorkflows ?? [];
  const typesOfWorkflowAuthors = uniq(
    workflows
      .map((workflow) =>
        workflow?.gitContext?.committer
          ? WorkflowAuthorTypes.GIT
          : workflow?.triggeredBy?.__typename === 'User'
            ? WorkflowAuthorTypes.USER
            : null,
      )
      .filter((x) => x !== null),
  );

  const hasGitAuthors = typesOfWorkflowAuthors.some(
    (authorType) => authorType === WorkflowAuthorTypes.GIT,
  );
  const hasUserAuthors = typesOfWorkflowAuthors.some(
    (authorType) => authorType === WorkflowAuthorTypes.USER,
  );
  const hasCommitIds = workflows.some(
    (workflow) => !!workflow.gitContext?.commit,
  );

  const pageHasFilters =
    isMinLengthArray(1, selectedAuthors) ||
    isMinLengthArray(1, selectedBranches) ||
    isMinLengthArray(1, selectedSubgraphs) ||
    (!isVariantTab && isMinLengthArray(1, selectedVariants)) ||
    !!status;
  const serviceHasWorkflows =
    (data?.service?.singleUnfilteredCheckWorkflow.length ?? 0) > 0;

  const isFederated = data?.service?.variant?.hasManagedSubgraphs;
  // nosemgrep
  const roverCheckCommand = `rover ${
    isFederated ? 'subgraph' : 'graph'
  } check ${graphRefToString({ graphId, graphVariant })}${
    isFederated ? ` --name <subgraph name>` : ''
  } --schema <path to schema.gql>`;
  const roverIntrospectCommand = `rover ${
    isFederated ? 'subgraph' : 'graph'
  } introspect http://localhost:4000 | rover ${
    isFederated ? 'subgraph' : 'graph'
  } check ${graphRefToString({ graphId, graphVariant })}${
    isFederated ? ` --name <subgraph name>` : ''
  } --schema -`;

  const onToggleDrawer = useCallback(
    (newId: string) => {
      const newState = new Set(expandedWorkflowIds);
      if (newState.has(newId)) {
        newState.delete(newId);
      } else {
        newState.add(newId);
      }
      setExpandedWorkflowIds(newState);
    },
    [expandedWorkflowIds],
  );

  const trackCustomEvent = useTrackCustomEvent();
  useEffect(() => {
    trackCustomEvent({
      category: 'Sandbox Check',
      action: 'Toggled sandbox checks',
      label: showSandboxChecks ? 'on' : 'off',
    });
  }, [showSandboxChecks, trackCustomEvent]);
  useEffect(() => {
    if (pollingError) {
      // There is an error that occurs on this page with the polling query.
      // Instead of throwing it, we should capture this error with sentry
      Sentry.captureException(pollingError);
    }
  }, [pollingError]);
  useEffect(() => {
    if (error) {
      // TODO: This should probably render an error page instead now that it doesn't poll
      Sentry.captureException(error);
    }
  }, [error]);

  if (loading) {
    return <Loading />;
  }
  const getSettingsLink = (
    tab:
      | 'checks_custom_checks'
      | 'checks_operations'
      | 'checks_linter'
      | 'checks_proposals' = 'checks_operations',
  ): LocationDescriptorObject =>
    view === 'graph'
      ? graphSettingsRouteConfig.locationFrom({
          location,
          patch: { tab },
          fromRouteConfig: recentChecksRouteConfig,
        })
      : view === 'variant'
        ? variantSettingsRouteConfig.locationFrom({
            location,
            patch: { tab },
            fromRouteConfig: recentChecksRouteConfig,
          })
        : assertUnreachableOrReturnDefault(
            view,
            variantSettingsRouteConfig.locationFrom({
              location,
              patch: { tab },
              fromRouteConfig: recentChecksRouteConfig,
            }),
          );
  if (!workflows.length) {
    return (
      <Card
        isDivided={serviceHasWorkflows && pageHasFilters}
        className="m-8 h-full text-center"
      >
        {serviceHasWorkflows && pageHasFilters && (
          <CardHeader>
            <CardTitle>Recent checks</CardTitle>
          </CardHeader>
        )}
        <CardBody>
          <div className="flex h-full items-center justify-center">
            <div
              className={classnames(
                'mx-6 py-6',
                serviceHasWorkflows && pageHasFilters
                  ? 'max-w-sm'
                  : 'max-w-3xl',
              )}
            >
              {serviceHasWorkflows && pageHasFilters ? (
                <>
                  <Telescope className="mb-2 w-48" />
                  <div className="mb-4 text-3xl font-semibold">
                    No Matches found.
                  </div>
                  <div className="mb-5 text-base">
                    Try removing some filters to see your checks.
                  </div>
                  <Button
                    className="px-6"
                    variant="secondary"
                    as={ReactRouterLink}
                    to={recentChecksRouteConfig.locationFrom({
                      location,
                      patch: {
                        author: RouteConfig.DELETE,
                        subgraph: RouteConfig.DELETE,
                        branch: RouteConfig.DELETE,
                        status: RouteConfig.DELETE,
                        variants: RouteConfig.DELETE,
                      },
                    })}
                  >
                    Reset filters
                  </Button>
                </>
              ) : (
                <>
                  <Astronaut2 className="mb-2 w-32" />
                  <div className="mb-1 text-2xl font-semibold">
                    Run schema checks with the Rover CLI
                  </div>
                  <div className="mb-4 text-base text-secondary">
                    Setting up automation for checks with Rover will improve
                    your team’s efficiency.{' '}
                    <Link to={DocsRouter.path('SchemaValidation')} isExternal>
                      Learn more
                    </Link>
                  </div>
                  <div className="mb-4 text-left">
                    <div className="mb-1 text-base font-semibold text-primary">
                      Using a .gql schema file
                    </div>
                    <Code
                      code={roverCheckCommand}
                      showCopyButton
                      className="[&_code]:w-[95%] [&_code]:flex-initial"
                    />
                  </div>
                  <div className="mb-8 text-left">
                    <div className="mb-1 text-left text-base font-semibold text-primary">
                      Using introspection
                    </div>
                    <Code
                      code={roverIntrospectCommand}
                      showCopyButton
                      className="[&_code]:w-[95%] [&_code]:flex-initial"
                    />
                  </div>
                  <ButtonGroup>
                    <Button
                      leftIcon={<IconSettings />}
                      variant="secondary"
                      as={ReactRouterLink}
                      to={getSettingsLink()}
                    >
                      Configure checks
                    </Button>
                    <Button
                      leftIcon={<IconRover />}
                      variant="secondary"
                      as="a"
                      href={DocsRouter.path('RoverGettingStarted')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Install the Rover CLI
                    </Button>
                  </ButtonGroup>
                </>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      {showOpChecksDisabledAlertBanner &&
        !showOpAndLintChecksDisabledAlertBanner && (
          <ChecksDisabledAlertBanner
            description={
              <p className="pr-1">
                <span className="font-semibold">Operation checks</span> are
                currently <span className="font-semibold">disabled</span> for
                this variant.
              </p>
            }
            canQueryCheckConfiguration={canQueryCheckConfiguration}
            bannerLink={<ReactRouterLink to={getSettingsLink()} />}
          />
        )}
      {showLintChecksDisabledAlertBanner &&
        !showOpChecksDisabledAlertBanner && (
          <ChecksDisabledAlertBanner
            description={
              <p className="pr-1">
                <span className="font-semibold">Linter checks</span> are
                currently <span className="font-semibold">disabled</span> for
                this graph.
              </p>
            }
            canQueryCheckConfiguration={canQueryCheckConfiguration}
            bannerLink={<Link to={getSettingsLink('checks_linter')} />}
          />
        )}
      {showOpAndLintChecksDisabledAlertBanner && (
        <ChecksDisabledAlertBanner
          description={
            <p className="pr-1">
              <span className="font-semibold">Operation</span> and{' '}
              <span className="font-semibold">linter checks</span> are currently{' '}
              <span className="font-semibold">disabled</span>.
            </p>
          }
          canQueryCheckConfiguration={canQueryCheckConfiguration}
          bannerLink={<Link to={getSettingsLink()} />}
        />
      )}
      <Card className="m-8 mt-4">
        <CardHeader>
          <CardTitle>Recent checks</CardTitle>
          <CardHeaderControl>
            {workflows.some((workflow) => workflow.isSandboxCheck) && (
              <Switch
                isChecked={showSandboxChecks}
                data-testid="hide-sandbox-checks-switch"
                onChange={() => setShowSandboxChecks(!showSandboxChecks)}
              >
                {showSandboxChecks
                  ? 'Hide sandbox checks'
                  : 'Show sandbox checks'}
              </Switch>
            )}
            <ButtonGroup>
              <Button
                leftIcon={<IconSettings />}
                variant="secondary"
                as={ReactRouterLink}
                to={getSettingsLink()}
              >
                Configuration
              </Button>
              <Tooltip label="View documentation" placement="top">
                <IconButton
                  aria-label="View documentation"
                  variant="secondary"
                  as="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={DocsRouter.path('SchemaValidation')}
                >
                  <IconBook />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
          </CardHeaderControl>
        </CardHeader>
        <CardBody className="px-0 pb-0">
          <div className="grid grid-cols-9 gap-x-2">
            <h3 className="col-span-3 pl-6 text-xs font-semibold uppercase leading-loose text-neutral">
              Workflow
            </h3>
            <h3 className="col-span-2 text-xs font-semibold uppercase leading-loose text-neutral">
              Tasks
            </h3>
            <h3 className="col-span-2 text-xs font-semibold uppercase leading-loose text-neutral">
              Branch
            </h3>
            <h3 className="col-span-1 text-xs font-semibold uppercase leading-loose text-neutral">
              <Tooltip
                disabled={hasUserAuthors && !hasGitAuthors}
                interactive
                placement="bottom-start"
                label={
                  hasUserAuthors ? (
                    <span className="font-normal normal-case">
                      If this check was triggered from a GitHub PR or git
                      branch, this is the author of the last Git commit on the
                      branch. If this check was triggered from Apollo Studio,
                      this is the user who triggered it.
                      <br />
                      <Link
                        isExternal
                        to={DocsRouter.path('IntegrateChecksWithGitHub')}
                      >
                        Learn how to integrate schema checks with your GitHub
                        PRs.
                      </Link>
                    </span>
                  ) : (
                    <span className="font-normal normal-case">
                      This is the author of the last Git commit on the branch.
                      <br />
                      <Link
                        isExternal
                        to={DocsRouter.path('IntegrateChecksWithGitHub')}
                      >
                        Learn how to integrate schema checks with your GitHub
                        PRs.
                      </Link>
                    </span>
                  )
                }
              >
                <span className="col-span-1 flex items-center text-xs font-semibold uppercase leading-loose text-neutral">
                  {!hasUserAuthors ? 'Git Author' : 'Author'}
                  {!(hasUserAuthors && !hasGitAuthors) && (
                    <IconInfo className="ml-2 text-icon-primary" />
                  )}
                </span>
              </Tooltip>
            </h3>
            {hasCommitIds && (
              <h3 className="col-span-1 pr-6 text-xs font-semibold uppercase leading-loose text-neutral">
                Commit ID
              </h3>
            )}
            <div className="col-span-9 my-1" />
            {workflows
              .filter(
                (workflow) =>
                  // if sandbox checks is toggled on, don't filter out any - otherwise filter out sandbox checks
                  showSandboxChecks || !workflow.isSandboxCheck,
              )
              .map((workflow) => {
                const displayName = workflow?.gitContext?.committer
                  ? committerDisplayName(workflow.gitContext.committer)
                  : workflow?.triggeredBy?.__typename === 'User'
                    ? workflow.triggeredBy.fullName
                    : committerDisplayName('');

                const displayNameTooltipContent =
                  workflow.gitContext?.committer ??
                  (workflow?.triggeredBy?.__typename === 'User'
                    ? workflow.triggeredBy.fullName
                    : null);

                const thresholdCrossed: number | null | undefined = (
                  workflow.tasks.find(
                    (task) => task.__typename === 'OperationsCheckTask',
                  ) as Extract<
                    GraphQLTypes.CheckWorkflowTaskItemFragment,
                    { __typename: 'OperationsCheckTask' }
                  >
                )?.result?.crossedOperationThreshold;
                const crossedThreshold: boolean =
                  thresholdCrossed !== null && thresholdCrossed !== undefined;

                const hasOperationTask =
                  workflow.tasks.findIndex(
                    (task) => task.__typename === 'OperationsCheckTask',
                  ) > -1;
                const hasDownstreamTasks =
                  workflow.tasks.findIndex(
                    (task) => task.__typename === 'DownstreamCheckTask',
                  ) > -1;

                const displayedTasks = workflow.tasks.filter((task) =>
                  shouldDisplayTask(
                    task,
                    shouldDescriptionsWarn,
                    proposalsUseHasWarnings,
                  ),
                );

                return (
                  <React.Fragment key={workflow.id}>
                    <Divider className="col-span-9" />
                    <div className="col-span-9 -mx-2 -my-px rounded border border-transparent px-2 py-3 ">
                      <div className="grid grid-cols-9 gap-x-2">
                        <div className="col-span-3 pl-6">
                          <CheckWorkflowItem
                            workflow={workflow}
                            isDrawerOpen={expandedWorkflowIds.has(workflow.id)}
                            onToggleDrawer={onToggleDrawer}
                          />
                        </div>
                        <div className="col-span-2 -ml-2">
                          <Tooltip
                            disabled={!crossedThreshold}
                            label={
                              <div>
                                We have detected more than{' '}
                                {format.requests(thresholdCrossed)} unique
                                operations. Any additional operations beyond
                                these have not been checked and may be affected.
                              </div>
                            }
                          >
                            <div>
                              {displayedTasks.length === 0 ? (
                                <SuccessfulCheckWorkflowItem
                                  workflow={workflow}
                                />
                              ) : (
                                displayedTasks.map((task) => (
                                  <CheckWorkflowTaskItem
                                    className="mr-2 mt-2"
                                    task={task}
                                    key={task.id}
                                    crossedThreshold={crossedThreshold}
                                  />
                                ))
                              )}

                              {!hasOperationTask && (
                                <div className="mx-2 mt-2 flex items-center">
                                  <TaskStatusIcon
                                    className="size-4"
                                    task={
                                      GraphQLTypes.CheckWorkflowTaskStatus
                                        .BLOCKED
                                    }
                                  />
                                  <p className="ml-2 text-base text-secondary">
                                    Operations Checks Disabled
                                  </p>
                                </div>
                              )}
                            </div>
                          </Tooltip>
                        </div>
                        <div className="col-span-2">
                          <div className="mt-2 flex items-center truncate text-base text-secondary">
                            <IconBranch className="mr-2 flex-shrink-0 text-icon-primary" />
                            <span className="truncate">
                              {workflow.isSandboxCheck
                                ? workflow.introspectionEndpoint
                                : (workflow.gitContext?.branch ??
                                  'Branch not set')}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="mt-2 flex items-center pr-1 text-base text-secondary">
                            <IconTeam className="mr-2 flex-shrink-0 text-icon-primary" />
                            <Tooltip label={displayNameTooltipContent}>
                              <span className="truncate">{displayName}</span>
                            </Tooltip>
                          </div>
                        </div>
                        {hasCommitIds && (
                          <div className="col-span-1 mt-2 pr-6">
                            {workflow.gitContext?.commit ? (
                              <ClickableText
                                as="a"
                                href={workflow.gitContext.commitUrl ?? ''}
                                className={classnames('flex items-center', {
                                  'font-semibold':
                                    !!workflow.gitContext.commitUrl,
                                })}
                                disabled={!workflow.gitContext.commitUrl}
                              >
                                {workflow.gitContext.commitUrl?.includes(
                                  'github.com',
                                ) ? (
                                  <IconGitHubSolid className="flex-shrink-0 pr-2" />
                                ) : (
                                  <IconCommit className="flex-shrink-0 pr-2" />
                                )}
                                <div className="truncate">
                                  {workflow.gitContext.commit.substring(0, 7)}
                                </div>
                              </ClickableText>
                            ) : (
                              <p className="text-base text-secondary">
                                no commit set
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {hasDownstreamTasks &&
                      expandedWorkflowIds.has(workflow.id) && (
                        <div className="col-span-9 -my-px border-t border-primary bg-secondary px-8 py-3 hover:border-primary">
                          <div className="grid grid-cols-9">
                            <div className="col-span-9 -mx-2">
                              <DownstreamCheckWorkflowList
                                workflow={workflow}
                                graphId={graphId}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                  </React.Fragment>
                );
              })}
          </div>
        </CardBody>
      </Card>
    </>
  );
};
